// biome-ignore-all lint/a11y/noLabelWithoutControl: staff-only admin surface; captions label the adjacent select (same convention as the other admin screens).
//
// =============================================================================
// TopUpProductMappingAdmin — Manage → Sales Order Sync → Top-up Requests →
//                            Product Mapping
// =============================================================================
// Admin/SuperUser screen that assigns each Stock-Top-up section (and per-year
// Stationery Pack, and the Other-Stock $150 variant) an Unleashed product code
// PICKED FROM THE CATALOGUE (`unleashed_products`) — the same pattern as the fit-out
// Product Mapping screen. Rows come from `top_up_product_map` (db-158). The SO suffix
// is NOT here — it's a per-section concept on the SO Rules screen (db-160). The Top-up
// SO builder reads this at Confirm & Send. Reference: docs/stock-top-ups-plan.md §E/§G.
// =============================================================================
import React from "react";
import { listTopUpProductMap, updateTopUpProductMapById } from "../../data/topUpProductMap";
import { listProducts } from "../../data/unleashedProducts";
import { logAudit } from "../../utils/auditLog";

// Display order of the mapping rows, grouped by heading.
const GROUPS = [
  { heading: "Stationery Packs (by school year)", sections: ["packs"] },
  { heading: "Bulk sections", sections: ["bulk", "bags", "hats", "socks", "other"] },
];

// A friendly label for a mapping row.
function rowLabel(section, itemKey) {
  switch (section) {
    case "packs":
      return `Stationery Packs — ${itemKey}`;
    case "bulk":
      return "Bulk Stationery";
    case "bags":
      return "School Bags";
    case "hats":
      return "Hats";
    case "socks":
      return "Socks";
    case "other":
      return itemKey === "reimbursement"
        ? "Other Stock — $150 Reimbursement"
        : "Other Stock — Lunch Boxes / Water Bottles";
    default:
      return `${section}${itemKey ? ` — ${itemKey}` : ""}`;
  }
}

export function TopUpProductMappingAdmin() {
  const [rows, setRows] = React.useState([]);
  const [products, setProducts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [savedMsg, setSavedMsg] = React.useState("");
  const [tick, setTick] = React.useState(0);

  React.useEffect(
    function () {
      let cancelled = false;
      setLoading(true);
      Promise.all([listTopUpProductMap(true), listProducts()]).then(function (res) {
        if (cancelled) return;
        setRows(res[0] || []);
        setProducts(res[1] || []);
        setLoading(false);
      });
      return function () {
        cancelled = true;
      };
    },
    [tick]
  );

  function showSaved(msg) {
    setSavedMsg(msg);
    setTimeout(function () {
      setSavedMsg("");
    }, 3000);
  }

  // Catalogue options for the dropdowns, grouped by product_group (active only) —
  // the same shape the fit-out Product Mapping uses.
  const productGroups = React.useMemo(
    function () {
      const active = products.filter(function (p) {
        return !p.deactivated;
      });
      const groups = new Map();
      for (const p of active) {
        const g = p.product_group || "Other";
        if (!groups.has(g)) groups.set(g, []);
        groups.get(g).push(p);
      }
      return [...groups.keys()]
        .sort(function (a, b) {
          return a.localeCompare(b);
        })
        .map(function (g) {
          return {
            group: g,
            items: groups.get(g).sort(function (a, b) {
              return a.product_code.localeCompare(b.product_code);
            }),
          };
        });
    },
    [products]
  );

  async function onPick(row, productCode) {
    if (!productCode) return;
    const { error } = await updateTopUpProductMapById(row.id, { product_code: productCode });
    if (error) {
      showSaved(`Save failed: ${error.message}`);
      return;
    }
    logAudit("top_up_product_map_set", {
      section: row.section,
      item_key: row.item_key,
      product_code: productCode,
    });
    setTick(function (n) {
      return n + 1;
    });
    showSaved("Mapping saved ✓");
  }

  const bySection = React.useMemo(
    function () {
      // Keep the DB order (sort_order) within each section.
      const m = {};
      for (const r of rows) {
        if (!m[r.section]) m[r.section] = [];
        m[r.section].push(r);
      }
      return m;
    },
    [rows]
  );

  const needsAttention = rows.filter(function (r) {
    return !r.product_code;
  }).length;

  function renderRow(r, idx, total) {
    const current = r.product_code || "";
    const attention = !current;
    return (
      <div
        key={r.id}
        className="grid items-center gap-3 py-[9px] px-[13px]"
        style={{
          gridTemplateColumns: "260px 1fr 150px",
          borderBottom: idx < total - 1 ? "1px solid #CFE0DE" : "none",
          background: attention ? "#FFF7E6" : idx % 2 === 0 ? "#FFFFFF" : "#F7F9F9",
        }}
      >
        <label className="text-[13px] text-zephyr-text font-semibold">
          {rowLabel(r.section, r.item_key)}
        </label>
        <select
          aria-label={`Product for ${rowLabel(r.section, r.item_key)}`}
          value={current}
          onChange={function (e) {
            onPick(r, e.target.value);
          }}
          className="py-[6px] px-[9px] rounded-[6px] border border-zephyr-border text-[13px] text-zephyr-text bg-white w-full"
          style={{ fontFamily: "inherit" }}
        >
          <option value="">— not mapped —</option>
          {productGroups.map(function (g) {
            return (
              <optgroup key={g.group} label={g.group}>
                {g.items.map(function (p) {
                  return (
                    <option key={p.product_code} value={p.product_code}>
                      {p.product_code}
                      {p.product_description ? ` — ${p.product_description}` : ""}
                    </option>
                  );
                })}
              </optgroup>
            );
          })}
        </select>
        <div>
          {attention ? (
            <span className="text-[11px] font-bold text-white bg-[#E0A000] rounded-[3px] py-[2px] px-[7px]">
              NEEDS CODE
            </span>
          ) : (
            <span className="text-[12px] text-[#2E7D32] font-semibold">✓ mapped</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[var(--page-max)] mx-auto">
      <div className="mb-1 text-[12px] text-zephyr-muted font-semibold">
        Sales Order Sync · Top-up Requests
      </div>
      <div className="flex justify-between items-center mb-3">
        <h1 className="m-0 text-[22px] text-zephyr-text">Top-up Product Mapping</h1>
        {savedMsg && (
          <span
            className={`text-[13px] font-semibold ${
              savedMsg.includes("failed") ? "text-[#C62828]" : "text-[#2E7D32]"
            }`}
          >
            {savedMsg}
          </span>
        )}
      </div>
      <p className="m-0 mb-2 text-[13px] text-zephyr-muted">
        Each Stock-Top-up section maps to one Unleashed <strong>product code</strong>, which the SO
        builder puts on the Sales Order line. The SO <strong>suffix</strong> (STA / BSTA / SBAG …)
        and how each section becomes SOs are set on the <strong>SO Rules</strong> screen.
      </p>
      {needsAttention > 0 && (
        <div className="mb-4 text-[13px] text-[#8a5a00] bg-[#FFF7E6] border border-[#F0C36D] rounded-[6px] py-2 px-3">
          <strong>{needsAttention}</strong> section{needsAttention === 1 ? "" : "s"} still need a
          product code. Pick the correct Unleashed product for each.
        </div>
      )}

      {loading ? (
        <div className="text-center text-zephyr-muted p-10">Loading…</div>
      ) : (
        GROUPS.map(function (grp) {
          const grpRows = grp.sections.flatMap(function (s) {
            return bySection[s] || [];
          });
          if (grpRows.length === 0) return null;
          return (
            <div key={grp.heading} className="mb-6">
              <h2 className="m-0 mb-2 text-[15px] text-zephyr-teal-dark font-bold">
                {grp.heading}
              </h2>
              <div className="border border-zephyr-border rounded-[10px] overflow-hidden">
                {grpRows.map(function (r, idx) {
                  return renderRow(r, idx, grpRows.length);
                })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
